"use client";

import * as React from "react";
import { usePathname } from "next/navigation";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { PanelRightOpen } from "lucide-react";
import { SheetTrigger } from "../ui/sheet";

// Helper function to generate breadcrumbs based on the current path
function generateBreadcrumbs(pathname: string) {
  const paths = pathname.split("/").filter(Boolean);

  if (paths.length === 1) {
    // Just '/c' route - no breadcrumbs needed
    return [];
  }

  // Handle c2 routes differently
  if (paths[0] === "c2") {
    const segments = paths.slice(1);
    if (segments[0] === "tunings") {
      if (segments.length === 1) {
        // Just '/c2/tunings'
        return [{ label: "Tunings", href: "/c2/tunings", isPage: true }];
      } else {
        // '/c2/tunings/slug' or deeper
        return [
          { label: "Tunings", href: "/c2/tunings", isPage: false },
          { label: segments[1], href: `/c2/tunings/${segments[1]}`, isPage: segments.length === 2 },
        ];
      }
    }
  }

  // 'c' is always the first part of the path, so we skip it
  const segments = paths.slice(1);

  // Handle different sections
  switch (segments[0]) {
    case "playground":
      return [{ label: "Playground", href: "/c/playground", isPage: true }];

    case "moondream-server":
      return [
        {
          label: "Moondream Server",
          href: "/c/moondream-server",
          isPage: true,
        },
      ];

    case "api-keys":
      return [
        { label: "Cloud", href: "/c/cloud", isPage: false },
        { label: "API Keys", href: "/c/cloud/api-keys", isPage: true },
      ];

    case "cloud":
      let label;
      const subsection = segments[1];
      switch (subsection) {
        case "api-keys":
          label = "API Keys";
          break;
        case "usage":
          label = "Usage";
          break;
      }
      return [
        { label: "Cloud", href: "/c/cloud", isPage: false },
        { label, href: `/c/cloud/${subsection}`, isPage: true },
      ];

    case "docs":
      if (segments.length === 1) {
        // Just '/c/docs'
        return [{ label: "Documentation", href: "/c/docs", isPage: true }];
      } else {
        // '/c/docs/something'
        const subsection = segments[1];
        let label = "";

        // Properly capitalize the labels
        switch (subsection) {
          case "quickstart":
            label = "Quickstart";
            break;
          case "capabilities":
            label = "Capabilities";
            break;
          case "advanced":
            label = "Advanced";
            break;
          case "changelog":
            label = "Changelog";
            break;
          default:
            label = subsection.charAt(0).toUpperCase() + subsection.slice(1);
        }

        return [
          { label: "Documentation", href: "/c/docs", isPage: false },
          { label, href: `/c/docs/${subsection}`, isPage: true },
        ];
      }

    default:
      // For any other routes, just show the current page
      return [
        {
          label: segments[0].charAt(0).toUpperCase() + segments[0].slice(1),
          href: `/c/${segments[0]}`,
          isPage: true,
        },
      ];
  }
}

export function ConsoleBreadcrumbs() {
  const pathname = usePathname();
  const breadcrumbs = generateBreadcrumbs(pathname);

  if (breadcrumbs.length === 0) {
    return null;
  }

  return (
    <Breadcrumb className="pb-4">
      <BreadcrumbList>
        <SheetTrigger className="block md:hidden">
          <PanelRightOpen className="color-black w-4 h-4 hover:cursor-pointer" />
        </SheetTrigger>
        <BreadcrumbSeparator className="md:hidden" />
        {breadcrumbs.map((crumb, index) => (
          <React.Fragment key={crumb.href}>
            <BreadcrumbItem className="">
              {crumb.isPage ? (
                <BreadcrumbPage className="block">{crumb.label}</BreadcrumbPage>
              ) : (
                <BreadcrumbLink href={crumb.href}>{crumb.label}</BreadcrumbLink>
              )}
            </BreadcrumbItem>
            {index < breadcrumbs.length - 1 && <BreadcrumbSeparator />}
          </React.Fragment>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  );
}
